import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useFullscreen } from '../hooks/useFullscreen';
import { useSpotify } from '../context/SpotifyContext';
import { HitsterGenerationPeriodId, HitsterRoundVariant } from '../types';
import './Display.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface DisplaySong {
  id: string;
  title: string;
  artist: string;
  year: number;
  order?: number;
  imageUrl?: string;
  youtubeVideoId?: string;
  youtubeStartTime?: number; // Start time in seconds
  youtubeEndTime?: number;   // End time in seconds
  audioStartTime?: number;   // Start time in seconds for audio/preview files
  volumeGain?: number;       // Volume multiplier (0.1 to 2.0, default 1.0)
  audioUrl?: string;
  previewUrl?: string;
  lyrics?: string;
  showLyrics?: boolean;
  spotifyUri?: string;
  yearSource?: string;  // URL or text to verify the release year
}

// Quiz question for pubquiz mode
interface DisplayQuizQuestion {
  id: string;
  type: 'multiple_choice' | 'open' | 'true_false' | 'number' | 'music' | 'rebus' | 'video';
  question: string;
  options?: string[];
  correctAnswer: string;
  points: number;
  timeLimit?: number;
  imageUrl?: string;
  audioUrl?: string;
  spotifyUri?: string;
  youtubeVideoId?: string;
  youtubeStartTime?: number;
  youtubeEndTime?: number;
  hint?: string;
  showAnswer?: boolean;
  // Rebus fields
  rebusType?: 'emoji' | 'missing_words' | 'scrambled' | 'fragments';
  rebusContent?: string;
  rebusHints?: string[];
  // Spotlight fields
  spotlightEnabled?: boolean;
  spotlightSpeed?: 'slow' | 'medium' | 'fast';
  spotlightSize?: 'small' | 'medium' | 'large';
  // Puzzle fields
  puzzleEnabled?: boolean;
  puzzleGridSize?: 3 | 4 | 5;
  puzzleSpeed?: 'slow' | 'medium' | 'fast';
}

interface DisplayBingoCategory {
  id: string;
  label: string;
  description?: string;
  color?: string;
  icon?: string;
}

interface DisplayState {
  mode?: 'hitster' | 'pubquiz' | 'bingo' | 'rules';  // Display mode
  isPlaying: boolean;
  showResults: boolean;
  showBingoCelebration?: boolean;
  bingoCelebrationTitle?: string;
  bingoCelebrationSubtitle?: string;
  isIntermission?: boolean;
  intermissionMessage?: string;
  waitingMessage?: string | null;
  currentSong: DisplaySong | null;
  eventName: string;
  eventLogoUrl?: string;
  roundName: string;
  roundNumber: number;
  currentSongOrder?: number;
  hitsterRoundVariant?: HitsterRoundVariant;
  hitsterGenerationPeriodId?: HitsterGenerationPeriodId | null;
  hitsterGenerationPeriodLabel?: string | null;
  hitsterGenerationPeriodIconUrl?: string | null;
  timestamp: number;
  youtubeVolume?: number;
  youtubePaused?: boolean;
  showScoreboard?: boolean;
  scoreboardEventId?: string | null;
  // Round answers
  showRoundAnswers?: boolean;
  roundAnswersSongs?: { id: string; order: number; title: string; artist: string; year: number; imageUrl?: string; }[];
  roundAnswersRoundName?: string;
  roundAnswersStartYear?: number | null;
  // PubQuiz fields
  quizQuestion?: DisplayQuizQuestion;
  quizName?: string;
  rulesEventId?: string | null;
  bingoView?: 'music' | 'hitster-spinner';
  bingoHitsterDifficulty?: 'beginner' | 'expert' | 'mixed';
  bingoHitsterCategories?: DisplayBingoCategory[];
  bingoSelectedCategoryId?: string | null;
  bingoBeamAngle?: number;
  bingoIsSpinning?: boolean;
  bingoSpinDurationMs?: number;
  bingoPlayDurationMs?: number;  // Duration to play each bingo song in milliseconds (default: 25000)
  bingoPushNonce?: number;
}

const isImageIcon = (icon?: string): boolean => !!icon && /\.(png|jpe?g|webp|svg)$/i.test(icon);

// Parse lyrics with timestamps
interface TimedLine {
  text: string;
  time: number; // time in seconds
}

function parseLyrics(lyrics: string): TimedLine[] {
  // First pass: parse all lines, marking timestamped vs non-timestamped
  const rawLines: Array<{ text: string; time: number | null }> = [];
  for (const line of lyrics.split('\n')) {
    if (!line.trim()) continue;
    const m = line.match(/^\[(\d+):(\d{2})\]\s*(.*)/);
    if (m) {
      rawLines.push({ text: m[3] || '', time: parseInt(m[1]) * 60 + parseInt(m[2]) });
    } else {
      rawLines.push({ text: line, time: null });
    }
  }

  // Second pass: fill in missing timestamps by interpolation so lines without
  // timestamps get evenly distributed between the surrounding anchors instead
  // of always getting a fixed 3 seconds (which made the first lines appear to
  // run faster when the rest of the song had longer per-line durations).
  const timedLines: TimedLine[] = [];
  let i = 0;
  while (i < rawLines.length) {
    if (rawLines[i].time !== null) {
      timedLines.push({ text: rawLines[i].text, time: rawLines[i].time as number });
      i++;
    } else {
      // Collect the whole group of consecutive non-timestamped lines
      const groupStart = i;
      while (i < rawLines.length && rawLines[i].time === null) i++;
      const count = i - groupStart;

      const prevTime = timedLines.length > 0 ? timedLines[timedLines.length - 1].time : 0;
      const nextTime = i < rawLines.length ? rawLines[i].time as number : null;

      if (nextTime !== null) {
        // Distribute evenly between the previous and next anchor
        const step = (nextTime - prevTime) / count;
        for (let j = 0; j < count; j++) {
          timedLines.push({ text: rawLines[groupStart + j].text, time: prevTime + j * step });
        }
      } else {
        // No following timestamp – fall back to 3 seconds per line
        for (let j = 0; j < count; j++) {
          timedLines.push({ text: rawLines[groupStart + j].text, time: prevTime + j * 3 });
        }
      }
    }
  }

  return timedLines;
}

// Declare YT as global for TypeScript
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Puzzle Image Component - displays image as shuffling puzzle pieces
interface PuzzleImageProps {
  imageUrl: string;
  gridSize: 3 | 4 | 5;
  speed: 'slow' | 'medium' | 'fast';
}

const PuzzleImage: React.FC<PuzzleImageProps> = ({ imageUrl, gridSize, speed }) => {
  const [positions, setPositions] = useState<number[]>([]);
  const totalPieces = gridSize * gridSize;
  
  // Initialize positions
  useEffect(() => {
    const initialPositions = Array.from({ length: totalPieces }, (_, i) => i);
    setPositions(shuffleArray([...initialPositions]));
  }, [totalPieces]);
  
  // Shuffle array helper
  const shuffleArray = (arr: number[]) => {
    const newArr = [...arr];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  };
  
  // Periodically shuffle pieces
  useEffect(() => {
    const speedMs = speed === 'slow' ? 5000 : speed === 'fast' ? 1500 : 3000;
    
    const interval = setInterval(() => {
      setPositions(prev => {
        // Swap 2-3 random pieces
        const newPositions = [...prev];
        const swapCount = Math.floor(Math.random() * 2) + 2;
        for (let s = 0; s < swapCount; s++) {
          const i = Math.floor(Math.random() * totalPieces);
          const j = Math.floor(Math.random() * totalPieces);
          [newPositions[i], newPositions[j]] = [newPositions[j], newPositions[i]];
        }
        return newPositions;
      });
    }, speedMs);
    
    return () => clearInterval(interval);
  }, [speed, totalPieces]);
  
  const pieceSize = 100 / gridSize;
  
  return (
    <div 
      className={`puzzle-container puzzle-grid-${gridSize} puzzle-speed-${speed}`}
      style={{ 
        display: 'grid',
        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        gap: '2px',
        maxWidth: '500px',
        margin: '0 auto 20px'
      }}
    >
      {positions.map((originalIndex, currentIndex) => {
        const originalRow = Math.floor(originalIndex / gridSize);
        const originalCol = originalIndex % gridSize;
        
        return (
          <div
            key={currentIndex}
            className="puzzle-piece"
            style={{
              aspectRatio: '1',
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: `${gridSize * 100}%`,
              backgroundPosition: `${originalCol * pieceSize * (100 / (100 - pieceSize))}% ${originalRow * pieceSize * (100 / (100 - pieceSize))}%`,
              transition: `all ${speed === 'slow' ? '1s' : speed === 'fast' ? '0.3s' : '0.5s'} ease-in-out`,
              borderRadius: '4px',
            }}
          />
        );
      })}
    </div>
  );
};

const Display: React.FC = () => {
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  
  // Spotify context - music plays on THIS display screen
  const {
    isAuthenticated: spotifyAuthenticated,
    isPremium: spotifyPremium,
    isReady: spotifyReady,
    play: spotifyPlay,
    pause: spotifyPause,
    setVolume: spotifySetVolume,
    isPlaying: spotifyIsPlaying,
    currentTrack: spotifyCurrentTrack,
    deviceId: spotifyDeviceId,
    login: spotifyLogin,
    user: spotifyUser,
    error: spotifyError
  } = useSpotify();
  
  const [displayState, setDisplayState] = useState<DisplayState>({
    isPlaying: false,
    showResults: false,
    showBingoCelebration: false,
    isIntermission: false,
    currentSong: null,
    eventName: '',
    eventLogoUrl: undefined,
    roundName: '',
    roundNumber: 0,
    timestamp: 0
  });
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const karaokeRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const lastAudioPlaybackKeyRef = useRef<string | null>(null);
  const youtubePlayerRef = useRef<any>(null);
  const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
  const youtubeContainerRef = useRef<HTMLDivElement | null>(null);
  const lastYoutubeVolumeRef = useRef<number>(100);
  const lastYoutubePausedRef = useRef<boolean>(false);
  const [ytApiReady, setYtApiReady] = useState(false);
  const [ytPlayerReady, setYtPlayerReady] = useState(false);
  const currentYoutubeVideoIdRef = useRef<string | null>(null);
  const spotifyGainCacheRef = useRef<Record<string, number>>({});
  
  // Mode indicator visibility
  const [showModeIndicator, setShowModeIndicator] = useState(false);
  const lastModeRef = useRef<string | undefined>(undefined);
  const [hitsterTipMsLeft, setHitsterTipMsLeft] = useState<number | null>(null);
  const lastHitsterTipPlaybackKeyRef = useRef<string | null>(null);
  const hitsterTipEndAtRef = useRef<number | null>(null);
  const hitsterTipStopRequestedRef = useRef(false);

  // Round answers: zoomed card
  const [zoomedCard, setZoomedCard] = useState<{ order: number; year: number; title: string; artist: string; } | null>(null);

  // Keyboard navigation for zoomed card
  React.useEffect(() => {
    if (!zoomedCard || !displayState.roundAnswersSongs) return;
    const sorted = [...displayState.roundAnswersSongs].sort((a, b) => (a.year || 0) - (b.year || 0));
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setZoomedCard(null); return; }
      const idx = sorted.findIndex(s => s.order === zoomedCard.order);
      if (e.key === 'ArrowLeft' && idx > 0) {
        const s = sorted[idx - 1]; setZoomedCard({ order: s.order, year: s.year, title: s.title, artist: s.artist });
      } else if (e.key === 'ArrowRight' && idx < sorted.length - 1) {
        const s = sorted[idx + 1]; setZoomedCard({ order: s.order, year: s.year, title: s.title, artist: s.artist });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomedCard, displayState.roundAnswersSongs]);

  // Spotify playback tracking
  const lastSpotifyPlaybackKeyRef = useRef<string | null>(null);
  const [spotifyPlaybackActive, setSpotifyPlaybackActive] = useState(false);
  const [spotifyRetryNonce, setSpotifyRetryNonce] = useState(0);

  // Scoreboard state
  interface TeamStanding {
    teamName: string;
    totalScore: number;
  }
  const [scoreboardStandings, setScoreboardStandings] = useState<TeamStanding[]>([]);
  const lastScoreboardEventIdRef = useRef<string | null>(null);

  // Fetch scoreboard standings when scoreboard mode is active
  useEffect(() => {
    if (!displayState.showScoreboard || !displayState.scoreboardEventId) {
      setScoreboardStandings([]);
      return;
    }

    const fetchStandings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${displayState.scoreboardEventId}`);
        if (!response.ok) return;
        const event = await response.json();

        const teams = event.teams || [];
        const rounds = event.rounds || [];

        const standings: TeamStanding[] = teams.map((team: any) => {
          const totalScore = rounds.reduce((sum: number, round: any) => {
            const rd = team.roundData?.find((r: any) => r.roundId === round.id);
            return sum + (rd?.totalScore || 0);
          }, 0);
          return { teamName: team.name, totalScore };
        });

        standings.sort((a, b) => b.totalScore - a.totalScore);
        setScoreboardStandings(standings);
      } catch (err) {
        console.error('Error fetching scoreboard:', err);
      }
    };

    fetchStandings();
    const interval = setInterval(fetchStandings, 10000);
    return () => clearInterval(interval);
  }, [displayState.showScoreboard, displayState.scoreboardEventId]);

  // ── Playback log helper ──
  const pushLog = async (
    level: 'info' | 'warn' | 'error' | 'ok',
    event: string,
    data?: any
  ) => {
    try {
      await fetch(`${API_BASE}/api/playback-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'display', level, event, data: data ?? null })
      });
    } catch (_) { /* ignore */ }
  };
  // ───────────────────────
  
  // Lyrics speed offset in seconds (negative = slower/delay, positive = faster)
  const [lyricsOffset, setLyricsOffset] = useState(() => {
    const saved = localStorage.getItem('hitster-lyrics-offset');
    return saved ? parseFloat(saved) : 0;
  });
  const [showSpeedControl, setShowSpeedControl] = useState(false);

  // Save offset to localStorage when changed
  useEffect(() => {
    localStorage.setItem('hitster-lyrics-offset', lyricsOffset.toString());
  }, [lyricsOffset]);

  useEffect(() => {
    const syncDisplayStatus = async () => {
      const expectedSpotifyUri = displayState.currentSong?.spotifyUri ?? null;
      const actualSpotifyUri = spotifyCurrentTrack?.uri ?? null;
      const reportedSpotifyUri = actualSpotifyUri ?? (
        expectedSpotifyUri && displayState.isPlaying && spotifyPlaybackActive
          ? expectedSpotifyUri
          : null
      );
      const playbackConfirmed = !!(
        expectedSpotifyUri &&
        reportedSpotifyUri === expectedSpotifyUri &&
        spotifyReady &&
        (spotifyPlaybackActive || spotifyIsPlaying)
      );

      try {
        await fetch(`${API_BASE}/api/display-state/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            spotifyAuthenticated,
            spotifyPremium,
            spotifyReady,
            isPlaying: displayState.isPlaying,
            spotifyPlaybackActive,
            playbackConfirmed,
            currentSongId: displayState.currentSong?.id ?? null,
            actualSpotifyUri: reportedSpotifyUri,
            deviceId: spotifyDeviceId,
            error: spotifyError ?? null
          })
        });
      } catch (err) {
        console.warn('Failed to sync display Spotify status:', err);
      }
    };

    syncDisplayStatus();
    const interval = setInterval(syncDisplayStatus, 1000);

    return () => clearInterval(interval);
  }, [spotifyAuthenticated, spotifyPremium, spotifyReady, displayState.isPlaying, displayState.currentSong?.id, displayState.currentSong?.spotifyUri, spotifyPlaybackActive, spotifyIsPlaying, spotifyCurrentTrack?.uri, spotifyDeviceId, spotifyError]);

  // Show mode indicator when mode changes, then hide after 3 seconds
  useEffect(() => {
    if (displayState.mode && displayState.mode !== lastModeRef.current) {
      lastModeRef.current = displayState.mode;
      setShowModeIndicator(true);
      
      const timer = setTimeout(() => {
        setShowModeIndicator(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [displayState.mode]);

  // Poll for display state
  useEffect(() => {
    const fetchState = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/display-state`);
        if (response.ok) {
          const data = await response.json();
          // Log when song changes to debug timing issues
          if (data.currentSong?.youtubeVideoId) {
            console.log('📺 Display state received:', {
              songId: data.currentSong?.id,
              title: data.currentSong?.title,
              youtubeStartTime: data.currentSong?.youtubeStartTime,
              youtubeEndTime: data.currentSong?.youtubeEndTime
            });
          }
          setDisplayState(prev => {
            // Reset lyrics position when song changes
            if (data.currentSong?.id !== prev.currentSong?.id) {
              setCurrentLineIndex(0);
              startTimeRef.current = Date.now();
            }
            return data;
          });
        }
      } catch (err) {
        console.error('Failed to fetch display state:', err);
      }
    };

    // Fetch immediately
    fetchState();

    // Poll faster during playback and while Hitster spinner is active for near-realtime sync.
    const spinnerRealtime = displayState.mode === 'bingo' && displayState.bingoView === 'hitster-spinner';
    const pollIntervalMs = displayState.isPlaying ? 250 : spinnerRealtime ? 120 : 1000;
    const interval = setInterval(fetchState, pollIntervalMs);

    return () => clearInterval(interval);
  }, [displayState.isPlaying, displayState.mode, displayState.bingoView]);

  useEffect(() => {
    const audio = audioRef.current;
    const song = displayState.currentSong;
    if (!audio || !song) return;

    const baseVolume = (displayState.youtubeVolume ?? 100) / 100;
    const volumeGain = song.volumeGain ?? 1.0;
    const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
    const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
    audio.volume = Math.min(1, Math.max(0, baseVolume * volumeGain * fadeFactor));
  }, [displayState.youtubeVolume, displayState.currentSong?.id, displayState.currentSong?.volumeGain, hitsterTipMsLeft]);

  // Audio playback - auto-play when song changes
  useEffect(() => {
    const song = displayState.currentSong;
    const isPlaying = displayState.isPlaying;
    // Use only song.id as the playback key. Including displayState.timestamp caused the song to
    // restart on every fade step (each volume update creates a new timestamp on the server).
    const playbackKey = song?.id ?? null;
    
    // Stop audio if not playing or no song
    if (!isPlaying || !song) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsAudioPlaying(false);
      setAudioError(null);
      lastAudioPlaybackKeyRef.current = null;
      return;
    }
    
    // Don't replay the same playback session
    if (playbackKey && playbackKey === lastAudioPlaybackKeyRef.current) return;
    lastAudioPlaybackKeyRef.current = playbackKey;
    
    // Try to play audio
    const audioUrl = song.audioUrl || song.previewUrl;
    if (audioUrl) {
      // Stop previous
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      setAudioError(null);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      // Apply volume gain
      const baseVolume = (displayState.youtubeVolume ?? 100) / 100;
      const volumeGain = song.volumeGain ?? 1.0;
      const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
      const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
      audio.volume = Math.min(1, Math.max(0, baseVolume * volumeGain * fadeFactor));

      audio.onplay = () => setIsAudioPlaying(true);
      audio.onpause = () => setIsAudioPlaying(false);
      audio.onended = () => {
        setIsAudioPlaying(false);
        fetch(`${API_BASE}/api/display-state/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).catch(() => {});
      };
      audio.onerror = () => {
        setAudioError('Audio kon niet worden geladen');
        setIsAudioPlaying(false);
      };

      // Apply start time: set currentTime after metadata loads, then play after seek
      if (song.audioStartTime && song.audioStartTime > 0) {
        const startSec = song.audioStartTime;
        audio.addEventListener('loadedmetadata', () => {
          audio.currentTime = startSec;
        }, { once: true });
        // Wait for seek to complete before playing
        audio.addEventListener('seeked', () => {
          audio.play().catch(err => {
            console.error('Autoplay blocked:', err);
            setAudioError('Klik om audio te starten');
          });
        }, { once: true });
        audio.load();
      } else {
        // No start time: play immediately
        audio.play().catch(err => {
          console.error('Autoplay blocked:', err);
          setAudioError('Klik om audio te starten');
        });
      }
    } else if (!song.youtubeVideoId && !song.spotifyUri) {
      // Only show error if there's no YouTube AND no Spotify
      setAudioError('Geen audio beschikbaar');
    }
    
    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [displayState.currentSong?.id, displayState.isPlaying, hitsterTipMsLeft, displayState.youtubeVolume]);

  // Audio pause control (for non-YouTube audio)  
  useEffect(() => {
    const paused = displayState.youtubePaused ?? false;
    const audio = audioRef.current;
    
    if (!audio) return;
    
    console.log('Audio pause control - paused:', paused, 'audio paused:', audio.paused);
    
    if (paused && !audio.paused) {
      console.log('Pausing audio');
      audio.pause();
    } else if (!paused && audio.paused && displayState.isPlaying) {
      console.log('Resuming audio');
      audio.play().catch(err => console.error('Play error:', err));
    }
  }, [displayState.youtubePaused, displayState.isPlaying]);

  // Spotify playback - play on THIS display screen (prioritize over YouTube if Spotify is available)
  useEffect(() => {
    const song = displayState.currentSong;
    const isPlaying = displayState.isPlaying;
    const paused = displayState.youtubePaused ?? false;
    // Use only song.id as the playback key. Including displayState.timestamp caused the song to
    // restart on every fade step (each volume update creates a new timestamp on the server).
    const playbackKey = song?.id ?? null;
    
    // Use Spotify if song has spotifyUri AND we're logged in with Premium.
    // Keep YouTube as fallback when a YouTube source is available.
    const canUseSpotify = spotifyAuthenticated && spotifyPremium && spotifyReady;
    const shouldUseSpotify = song?.spotifyUri && canUseSpotify && !song.audioUrl && !song.previewUrl && !song.youtubeVideoId;
    
    console.log('🎧 Display Spotify check:', { 
      hasSpotifyUri: !!song?.spotifyUri,
      spotifyAuthenticated, 
      spotifyPremium, 
      spotifyReady,
      canUseSpotify,
      shouldUseSpotify,
      isPlaying,
      songId: song?.id
    });
    
    // Stop Spotify if not playing or no song or shouldn't use Spotify
    if (!isPlaying || !song || !shouldUseSpotify) {
      if (spotifyPlaybackActive) {
        console.log('🎧 Display: Stopping Spotify playback');
        spotifyPause();
        setSpotifyPlaybackActive(false);
      }
      lastSpotifyPlaybackKeyRef.current = null;
      return;
    }
    
    // Handle pause/resume
    if (paused && spotifyPlaybackActive) {
      spotifyPause();
      return;
    }
    
    // Don't replay the same playback session
    if (playbackKey && playbackKey === lastSpotifyPlaybackKeyRef.current && !paused) {
      console.log('🎧 Display: Song already playing, skipping');
      return;
    }
    
    // Extra check - should never fail due to shouldUseSpotify check above
    if (!song.spotifyUri) return;
    
    // Play the song via Spotify on this display
    console.log('🎧 Display: Playing Spotify track:', song.spotifyUri);
    lastSpotifyPlaybackKeyRef.current = playbackKey;
    void pushLog('info', '▶ Spotify afspelen gestart op display', {
      spotifyUri: song.spotifyUri,
      songId: song.id,
      retryNonce: spotifyRetryNonce
    });

    const playWithAutoGain = async () => {
      const spotifyUri = song.spotifyUri;
      if (!spotifyUri) return;

      let effectiveGain = song.volumeGain;
      if (typeof effectiveGain !== 'number' && spotifyUri) {
        const spotifyId = spotifyUri.replace('spotify:track:', '');
        if (spotifyId) {
          const cached = spotifyGainCacheRef.current[spotifyId];
          if (typeof cached === 'number') {
            effectiveGain = cached;
          } else {
            try {
              const response = await fetch(`${API_BASE}/api/spotify/loudness/${spotifyId}`);
              if (response.ok) {
                const data = await response.json();
                if (typeof data.recommendedGain === 'number') {
                  spotifyGainCacheRef.current[spotifyId] = data.recommendedGain;
                  effectiveGain = data.recommendedGain;
                }
              }
            } catch (err) {
              console.warn('Display: could not fetch Spotify loudness:', spotifyId);
            }
          }
        }
      }

      const baseVolume = (displayState.youtubeVolume ?? 100) / 100;
      const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
      const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
      await spotifySetVolume(Math.max(0, Math.min(1, baseVolume * (effectiveGain ?? 1) * fadeFactor)));
      await spotifyPlay(spotifyUri);
    };

    playWithAutoGain().then(() => {
      console.log('🎧 Display: Spotify playback started successfully');
      void pushLog('ok', '✅ Spotify speelt op display');
      setSpotifyPlaybackActive(true);
    }).catch(err => {
      console.error('🎧 Display: Spotify play error:', err);
      void pushLog('error', '❌ Spotify afspeelfout op display', { error: String(err) });
      setSpotifyPlaybackActive(false);
      lastSpotifyPlaybackKeyRef.current = null;
      window.setTimeout(() => {
        setSpotifyRetryNonce((current) => current + 1);
      }, 1500);
    });
  // NOTE: displayState.timestamp and displayState.youtubeVolume are intentionally omitted.
  // Volume is handled by the dedicated volume-sync effect below, and timestamp changes on every
  // server write (including fade steps) – both would cause unwanted song restarts.
  }, [displayState.currentSong?.id, displayState.currentSong?.spotifyUri, displayState.isPlaying, displayState.youtubePaused, spotifyAuthenticated, spotifyPremium, spotifyReady, spotifyPlay, spotifyPause, spotifySetVolume, spotifyPlaybackActive, spotifyRetryNonce, hitsterTipMsLeft, displayState.youtubeVolume]);

  useEffect(() => {
    const song = displayState.currentSong;
    const canUseSpotify = spotifyAuthenticated && spotifyPremium && spotifyReady;
    const shouldUseSpotify = !!(song?.spotifyUri && canUseSpotify && !song.audioUrl && !song.previewUrl && !song.youtubeVideoId);

    if (!shouldUseSpotify || !spotifyPlaybackActive) {
      return;
    }

    let effectiveGain = song?.volumeGain;
    if (typeof effectiveGain !== 'number' && song?.spotifyUri) {
      const spotifyId = song.spotifyUri.replace('spotify:track:', '');
      const cached = spotifyGainCacheRef.current[spotifyId];
      if (typeof cached === 'number') {
        effectiveGain = cached;
      }
    }

    const baseVolume = (displayState.youtubeVolume ?? 100) / 100;
    const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
    const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
    spotifySetVolume(Math.max(0, Math.min(1, baseVolume * (effectiveGain ?? 1) * fadeFactor))).catch((err) => {
      console.warn('Display: could not update Spotify fade volume:', err);
    });
  }, [displayState.currentSong?.id, displayState.currentSong?.spotifyUri, displayState.currentSong?.audioUrl, displayState.currentSong?.previewUrl, displayState.currentSong?.volumeGain, displayState.youtubeVolume, displayState.bingoPlayDurationMs, spotifyAuthenticated, spotifyPremium, spotifyReady, spotifyPlaybackActive, spotifySetVolume, hitsterTipMsLeft]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setYtApiReady(true);
      return;
    }
    
    // Check if script is already loading
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      // Wait for API to be ready
      const checkReady = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setYtApiReady(true);
          clearInterval(checkReady);
        }
      }, 100);
      return () => clearInterval(checkReady);
    }
    
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    
    window.onYouTubeIframeAPIReady = () => {
      setYtApiReady(true);
    };
  }, []);

  // Create/destroy YouTube player
  useEffect(() => {
    const song = displayState.currentSong;
    
    // Check if Spotify should be used instead of YouTube
    const canUseSpotify = spotifyAuthenticated && spotifyPremium && spotifyReady;
    const spotifyTakesPriority = song?.spotifyUri && canUseSpotify && !song.audioUrl && !song.previewUrl && !song.youtubeVideoId;
    
    // Only use YouTube if no Spotify available, and song has youtubeVideoId
    const shouldPlay = displayState.isPlaying && song?.youtubeVideoId && !song.audioUrl && !song.previewUrl && !spotifyTakesPriority;
    const videoId = song?.youtubeVideoId || null;
    // Create a video key that includes start/end times to trigger player recreation when they change
    const videoKey = videoId ? `${videoId}-${song?.youtubeStartTime || 0}-${song?.youtubeEndTime || 0}` : null;
    
    console.log('🎬 YouTube player check:', { 
      hasYoutubeId: !!song?.youtubeVideoId, 
      spotifyTakesPriority, 
      shouldPlay 
    });
    
    // Only create/destroy when video ID or start/end times change
    if (shouldPlay && ytApiReady && youtubeContainerRef.current) {
      // Only recreate if video key changed (includes start/end times)
      if (videoKey !== currentYoutubeVideoIdRef.current) {
        currentYoutubeVideoIdRef.current = videoKey;
        setYtPlayerReady(false);
        
        // Destroy existing player
        if (youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current.destroy();
          } catch (e) {
            // Ignore
          }
          youtubePlayerRef.current = null;
        }
        
        // Clear container and create fresh div for player
        if (youtubeContainerRef.current) {
          youtubeContainerRef.current.innerHTML = '';
          
          // Small timeout to allow YouTube API to fully clean up from previous player
          const timeoutId = window.setTimeout(() => {
            if (!youtubeContainerRef.current) return; // Container was unmounted
            
            const playerDiv = document.createElement('div');
            playerDiv.id = 'yt-player-' + Date.now();
            youtubeContainerRef.current?.appendChild(playerDiv);
            
            // Create new player with optional start/end times
            const playerVarsConfig: any = {
              autoplay: 1,
              controls: 0,
              disablekb: 1,
              fs: 0,
              modestbranding: 1,
              rel: 0
            };
            
            // Add start time if specified
            if (song?.youtubeStartTime && song.youtubeStartTime > 0) {
              playerVarsConfig.start = Math.floor(song.youtubeStartTime);
            }
            
            // Add end time if specified
            if (song?.youtubeEndTime && song.youtubeEndTime > 0) {
              playerVarsConfig.end = Math.floor(song.youtubeEndTime);
            }
            
            console.log('🎬 Creating YouTube player with config:', {
              videoId,
              youtubeStartTime: song?.youtubeStartTime,
              youtubeEndTime: song?.youtubeEndTime,
              playerVarsConfig
            });
            
            youtubePlayerRef.current = new window.YT.Player(playerDiv, {
              videoId: videoId,
              playerVars: playerVarsConfig,
              events: {
                onReady: (event: any) => {
                  console.log('YouTube player ready');
                  // Reset refs so the pause/volume effect will apply current state
                  lastYoutubeVolumeRef.current = -1;
                  lastYoutubePausedRef.current = !displayState.youtubePaused; // Force trigger
                  setYtPlayerReady(true);
                },
                onStateChange: (event: any) => {
                  console.log('YouTube state change:', event.data);
                  setIsYoutubePlaying(event.data === window.YT.PlayerState.PLAYING);
                  // 0 = ended — reset display to QR screen automatically
                  if (event.data === 0) {
                    fetch(`${API_BASE}/api/display-state/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).catch(() => {});
                  }
                }
              }
            });
          }, 50); // 50ms delay for YouTube API cleanup
          
          // Cleanup on effect unmount or video key change
          return () => {
            clearTimeout(timeoutId);
          };
        }
      }
    } else if (!shouldPlay) {
      // Destroy player when not needed
      currentYoutubeVideoIdRef.current = null;
      setIsYoutubePlaying(false);
      setYtPlayerReady(false);
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.destroy();
        } catch (e) {
          // Ignore
        }
        youtubePlayerRef.current = null;
      }
      // Clear container
      if (youtubeContainerRef.current) {
        youtubeContainerRef.current.innerHTML = '';
      }
    }
  }, [displayState.currentSong?.youtubeVideoId, displayState.currentSong?.youtubeStartTime, displayState.currentSong?.youtubeEndTime, displayState.currentSong?.audioUrl, displayState.currentSong?.previewUrl, displayState.currentSong?.spotifyUri, displayState.isPlaying, ytApiReady, spotifyAuthenticated, spotifyPremium, spotifyReady]);

  // YouTube volume and pause control - apply every time state changes
  useEffect(() => {
    if (!ytPlayerReady || !youtubePlayerRef.current) return;
    
    const player = youtubePlayerRef.current;
    // Check if player methods are available
    if (typeof player.setVolume !== 'function' || typeof player.pauseVideo !== 'function') {
      console.log('YouTube player methods not ready yet');
      return;
    }
    
    const baseVolume = displayState.youtubeVolume ?? 100;
    const volumeGain = displayState.currentSong?.volumeGain ?? 1.0;
    const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
    const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
    const volume = Math.min(100, Math.max(0, Math.round(baseVolume * volumeGain * fadeFactor)));
    const paused = displayState.youtubePaused ?? false;
    
    console.log('YouTube control update - baseVolume:', baseVolume, 'volumeGain:', volumeGain, 'finalVolume:', volume, 'paused:', paused);
    
    // Always apply volume (with gain adjustment)
    player.setVolume(volume);
    
    // Always apply pause state - YouTube API handles this idempotently
    if (paused) {
      console.log('Pausing YouTube');
      player.pauseVideo();
    } else {
      console.log('Playing YouTube');
      player.playVideo();
    }
  }, [displayState.youtubeVolume, displayState.youtubePaused, displayState.currentSong?.volumeGain, ytPlayerReady, hitsterTipMsLeft]);

  // Manual play button for when autoplay is blocked
  const handleManualPlay = () => {
    const song = displayState.currentSong;
    const audioUrl = song?.audioUrl || song?.previewUrl;
    if (audioUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      // Apply volume gain
      const baseVolume = (displayState.youtubeVolume ?? 100) / 100;
      const volumeGain = song?.volumeGain ?? 1.0;
      const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
      const fadeFactor = hitsterTipMsLeft === null ? 1 : Math.max(0, Math.min(1, hitsterTipMsLeft / playDurationMs));
      audio.volume = Math.min(1, Math.max(0, baseVolume * volumeGain * fadeFactor));
      audio.onplay = () => { setIsAudioPlaying(true); setAudioError(null); };
      audio.onpause = () => setIsAudioPlaying(false);
      audio.onended = () => {
        setIsAudioPlaying(false);
        fetch(`${API_BASE}/api/display-state/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).catch(() => {});
      };
      if (song?.audioStartTime && song.audioStartTime > 0) {
        const startSec = song.audioStartTime;
        audio.addEventListener('loadedmetadata', () => {
          audio.currentTime = startSec;
        }, { once: true });
        audio.addEventListener('seeked', () => {
          audio.play();
        }, { once: true });
        audio.load();
      } else {
        audio.play();
      }
    }
  };

  const { isPlaying, showResults, currentSong, roundName, roundNumber, eventName } = displayState;
  const isIntermission = displayState.isIntermission ?? false;
  const eventLogoUrl = displayState.eventLogoUrl;
  const currentSongOrder = displayState.currentSongOrder ?? currentSong?.order;
  const bingoHitsterCategories = displayState.bingoHitsterCategories || [];
  const bingoSelectedCategory = bingoHitsterCategories.find((category) => category.id === displayState.bingoSelectedCategoryId);
  const bingoBeamAngle = displayState.bingoBeamAngle ?? -90;
  const isHitsterSpinnerView = displayState.mode === 'bingo' && displayState.bingoView === 'hitster-spinner';
  const spotifyPlaybackConfirmed = !!(
    currentSong?.spotifyUri && (
      spotifyPlaybackActive ||
      (spotifyIsPlaying && spotifyCurrentTrack?.uri === currentSong.spotifyUri)
    )
  );
  const hitsterPlaybackActive = !!(
    isHitsterSpinnerView &&
    currentSong &&
    displayState.isPlaying &&
    (isAudioPlaying || isYoutubePlaying || spotifyPlaybackConfirmed)
  );
  const hitsterTipSecondsLeft = hitsterTipMsLeft === null ? null : Math.ceil(hitsterTipMsLeft / 1000);
  const hitsterTipInnerWarning = hitsterTipSecondsLeft !== null && hitsterTipSecondsLeft > 0 && hitsterTipSecondsLeft <= 10;
  const hitsterTipOuterWarning = hitsterTipSecondsLeft !== null && hitsterTipSecondsLeft > 0 && hitsterTipSecondsLeft <= 5;
  const hitsterTipWarning = hitsterTipSecondsLeft !== null && hitsterTipSecondsLeft > 0 && hitsterTipSecondsLeft <= 5;

  useEffect(() => {
    const playbackKey = hitsterPlaybackActive && currentSong
      ? currentSong.id
      : null;

    if (!playbackKey) {
      lastHitsterTipPlaybackKeyRef.current = null;
      hitsterTipEndAtRef.current = null;
      hitsterTipStopRequestedRef.current = false;
      setHitsterTipMsLeft(null);
      return;
    }

    if (playbackKey === lastHitsterTipPlaybackKeyRef.current) {
      return;
    }

    lastHitsterTipPlaybackKeyRef.current = playbackKey;
    const playDurationMs = displayState.bingoPlayDurationMs ?? 25000;
    hitsterTipEndAtRef.current = Date.now() + playDurationMs;
    hitsterTipStopRequestedRef.current = false;
    setHitsterTipMsLeft(playDurationMs);
  }, [hitsterPlaybackActive, currentSong?.id]);

  useEffect(() => {
    if (hitsterTipMsLeft === null || hitsterTipMsLeft <= 0 || hitsterTipEndAtRef.current === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      const remainingMs = Math.max(0, hitsterTipEndAtRef.current! - Date.now());
      setHitsterTipMsLeft(remainingMs);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [hitsterTipMsLeft]);

  useEffect(() => {
    if (!hitsterPlaybackActive || hitsterTipMsLeft !== 0 || hitsterTipStopRequestedRef.current) {
      return;
    }

    hitsterTipStopRequestedRef.current = true;
    fetch(`${API_BASE}/api/display-state/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }).catch(() => {
      hitsterTipStopRequestedRef.current = false;
    });
  }, [hitsterPlaybackActive, hitsterTipMsLeft]);

  // Reset timer when leaving the Hitster spinner view.
  useEffect(() => {
    if (!isHitsterSpinnerView) {
      hitsterTipEndAtRef.current = null;
      hitsterTipStopRequestedRef.current = false;
      setHitsterTipMsLeft(null);
    }
  }, [isHitsterSpinnerView]);

  // Parse lyrics (only when showLyrics !== false)
  const lyricsLines = useMemo(() => {
    if (!currentSong?.lyrics) return [];
    if (currentSong.showLyrics === false) return [];
    return parseLyrics(currentSong.lyrics);
  }, [currentSong?.lyrics, currentSong?.showLyrics]);

  // Track paused time for lyrics sync
  const pausedAtRef = useRef<number | null>(null);
  const totalPausedTimeRef = useRef<number>(0);

  // Auto-scroll lyrics every 3 seconds per line
  useEffect(() => {
    const isPaused = displayState.youtubePaused ?? false;
    
    if (!isPlaying || lyricsLines.length === 0) {
      setCurrentLineIndex(0);
      startTimeRef.current = null;
      pausedAtRef.current = null;
      totalPausedTimeRef.current = 0;
      return;
    }

    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
      totalPausedTimeRef.current = 0;
    }

    // Handle pause/resume for lyrics timing
    if (isPaused) {
      if (pausedAtRef.current === null) {
        pausedAtRef.current = Date.now();
      }
      return; // Don't update lyrics while paused
    } else if (pausedAtRef.current !== null) {
      // Just resumed - add paused duration to total
      totalPausedTimeRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }

    const updateLine = () => {
      if (startTimeRef.current === null) return;
      
      // Subtract total paused time from elapsed time
      const elapsed = (Date.now() - startTimeRef.current - totalPausedTimeRef.current) / 1000;
      
      // Apply offset: negative offset = slower (lyrics come later)
      const adjustedElapsed = elapsed + lyricsOffset;
      
      // Find current line based on timestamps
      let lineIndex = 0;
      for (let i = 0; i < lyricsLines.length; i++) {
        if (lyricsLines[i].time <= adjustedElapsed) {
          lineIndex = i;
        } else {
          break;
        }
      }
      
      if (lineIndex < lyricsLines.length) {
        setCurrentLineIndex(lineIndex);
      }
    };

    const interval = setInterval(updateLine, 100);

    return () => clearInterval(interval);
  }, [isPlaying, lyricsLines.length, currentSong?.id, lyricsOffset, displayState.youtubePaused]);

  // Scroll to current line
  useEffect(() => {
    if (!karaokeRef.current) return;
    
    const container = karaokeRef.current;
    const lineElements = container.querySelectorAll('.karaoke-line');
    
    if (lineElements[currentLineIndex]) {
      const lineElement = lineElements[currentLineIndex] as HTMLElement;
      const containerHeight = container.clientHeight;
      const lineTop = lineElement.offsetTop;
      const lineHeight = lineElement.offsetHeight;
      const scrollTarget = lineTop - (containerHeight / 2) + (lineHeight / 2);
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    }
  }, [currentLineIndex]);

  return (
    <div className="display-page">
      <button className="fullscreen-btn" onClick={toggleFullscreen}>
        {isFullscreen ? '⬜ Exit Fullscreen' : '⬛ Fullscreen'}
      </button>
      
      {/* Lyrics speed control */}
      {lyricsLines.length > 0 && (
        <div className="lyrics-speed-control">
          <button 
            className="speed-toggle-btn"
            onClick={() => setShowSpeedControl(!showSpeedControl)}
            title="Songtekst snelheid aanpassen"
          >
            ⏱️
          </button>
          {showSpeedControl && (
            <div className="speed-panel">
              <span className="speed-label">Tekst vertraging:</span>
              <button onClick={() => setLyricsOffset(o => o - 1)}>-1s</button>
              <button onClick={() => setLyricsOffset(o => o - 0.5)}>-0.5s</button>
              <span className="speed-value">{lyricsOffset > 0 ? '+' : ''}{lyricsOffset}s</span>
              <button onClick={() => setLyricsOffset(o => o + 0.5)}>+0.5s</button>
              <button onClick={() => setLyricsOffset(o => o + 1)}>+1s</button>
              <button onClick={() => setLyricsOffset(0)} className="reset-btn">Reset</button>
            </div>
          )}
        </div>
      )}

      {/* Audio status indicator */}
      {isPlaying && currentSong && (
        <div className="audio-status">
          {/* Audio status indicators - hidden per user request */}
          {/* {isAudioPlaying && <span className="audio-playing">🔊 Audio speelt</span>} */}
          {/* {currentSong?.youtubeVideoId && !currentSong?.audioUrl && !currentSong?.previewUrl && ytPlayerReady && (
            <span className="audio-playing youtube">📺 YouTube speelt</span>
          )} */}
          {/* Spotify status - hidden per user request */}
          {/* {currentSong?.spotifyUri && !currentSong?.audioUrl && !currentSong?.previewUrl && !currentSong?.youtubeVideoId && (
            spotifyPlaybackActive || spotifyIsPlaying ? (
              <span className="audio-playing spotify">🎧 Spotify speelt</span>
            ) : spotifyAuthenticated && spotifyPremium && spotifyReady ? (
              <span className="audio-playing spotify loading">🎧 Spotify wordt gestart...</span>
            ) : (
              <span className="audio-playing spotify warning">⚠️ Log in bij Spotify op dit scherm</span>
            )
          )} */}
          {audioError && !currentSong?.spotifyUri && (
            <button className="audio-error-btn" onClick={handleManualPlay}>
              🔇 {audioError}
            </button>
          )}
        </div>
      )}
      
      {/* Spotify login panel for Display - shows when Spotify is needed but not ready */}
      {(!spotifyAuthenticated || !spotifyPremium || !spotifyReady) && displayState.currentSong?.spotifyUri && !displayState.currentSong?.audioUrl && displayState.isPlaying && (
        <div className="spotify-login-panel">
          <div className="spotify-login-content">
            <span className="spotify-icon">🎧</span>
            <span className="spotify-message">
              {!spotifyAuthenticated 
                ? 'Log in met Spotify Premium om muziek af te spelen op dit scherm'
                : !spotifyPremium 
                  ? 'Spotify Premium is vereist voor afspelen'
                  : !spotifyReady
                    ? 'Spotify player wordt geïnitialiseerd...'
                    : 'Spotify wordt geladen...'}
            </span>
            {!spotifyAuthenticated && (
              <button className="spotify-login-btn" onClick={spotifyLogin}>
                Inloggen met Spotify
              </button>
            )}
            {spotifyUser && (
              <span className="spotify-user">Ingelogd als: {spotifyUser.name}</span>
            )}
            {spotifyError && (
              <span className="spotify-error">Fout: {spotifyError}</span>
            )}
          </div>
        </div>
      )}

      {/* Hidden YouTube player container - always rendered */}
      <div
        ref={youtubeContainerRef}
        className="youtube-audio-player"
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />

      <div className="display-container">
        {/* Mode indicator - shows briefly when mode changes */}
        {showModeIndicator && displayState.mode && (
          <div className={`mode-indicator mode-${displayState.mode} fade-out`}>
            {displayState.mode === 'hitster' && '🎵 Hitster'}
            {displayState.mode === 'pubquiz' && '🧠 Quiz'}
            {displayState.mode === 'rules' && '📖 Speluitleg'}
          </div>
        )}
        {displayState.mode === 'rules' && displayState.rulesEventId ? (
          <div className="display-rules-shell">
            <iframe
              className="display-rules-frame"
              src={`/game-rules/${displayState.rulesEventId}?embedded=1`}
              title="Speluitleg"
            />
          </div>
        ) : displayState.showScoreboard ? (
          <div className="display-scoreboard-fullscreen">
            <div className="display-scoreboard-header">
              <span className="display-scoreboard-trophy">🏆</span>
              <h1 className="display-scoreboard-title">
                {displayState.eventName || 'Scorebord'}
              </h1>
              <span className="display-scoreboard-trophy">🏆</span>
            </div>
            <div className="display-scoreboard-columns">
              {(() => {
                const half = Math.ceil(scoreboardStandings.length / 2);
                const col1 = scoreboardStandings.slice(0, half);
                const col2 = scoreboardStandings.slice(half);
                const medalFor = (rank: number) => rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
                const renderEntry = (standing: TeamStanding, rank: number) => {
                  const medal = medalFor(rank);
                  return (
                    <div
                      key={standing.teamName}
                      className={`display-scoreboard-entry${medal ? ' display-scoreboard-medal' : ''}`}
                    >
                      <span className="display-scoreboard-rank">
                        {medal || `#${rank}`}
                      </span>
                      <span className="display-scoreboard-team">{standing.teamName}</span>
                      <span className="display-scoreboard-score">{standing.totalScore} pt</span>
                    </div>
                  );
                };
                return (
                  <>
                    <div className="display-scoreboard-col">
                      {col1.map((s, i) => renderEntry(s, i + 1))}
                    </div>
                    {col2.length > 0 && (
                      <div className="display-scoreboard-col">
                        {col2.map((s, i) => renderEntry(s, half + i + 1))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ) : displayState.showBingoCelebration ? (
          <div className="display-bingo-celebration">
            <div className="bingo-confetti-layer" aria-hidden="true">
              {Array.from({ length: 24 }, (_, index) => (
                <span
                  key={index}
                  className={`bingo-confetti-piece bingo-confetti-piece-${(index % 6) + 1}`}
                  style={{ left: `${(index * 4.2) % 100}%`, animationDelay: `${(index % 8) * 0.18}s` }}
                />
              ))}
            </div>
            <div className="bingo-celebration-badge">BINGO ALARM</div>
            <div className="bingo-celebration-title">
              {displayState.bingoCelebrationTitle || 'We hebben een bingo!'}
            </div>
            <div className="bingo-celebration-subtitle">
              {displayState.bingoCelebrationSubtitle || 'Kom naar voren, dan controleert de spelleider je kaart.'}
            </div>
            <div className="bingo-celebration-burst" aria-hidden="true">
              <span>🎉</span>
              <span>🎊</span>
              <span>✨</span>
              <span>🏆</span>
            </div>
          </div>
        ) : (
          <>
            <div className="display-header">
              {eventLogoUrl ? (
                <img src={eventLogoUrl} alt="Event logo" className="event-logo" />
              ) : displayState.mode === 'pubquiz' && displayState.quizName ? (
                <h1>🎯 {displayState.quizName}</h1>
              ) : displayState.mode === 'bingo' ? (
                <></>
              ) : eventName ? (
                <h1>🎵 {eventName}</h1>
              ) : (
                <h1>🎵 Hitster</h1>
              )}
            </div>

            {roundNumber > 0 && displayState.mode !== 'pubquiz' && !(displayState.mode === 'bingo' && displayState.bingoView === 'hitster-spinner') && (
              <div className="round-display">
                {roundName || `Ronde #${roundNumber}`}
              </div>
            )}
            {displayState.mode === 'hitster' && displayState.hitsterGenerationPeriodLabel && (
              <div className="round-display generation-display">
                {displayState.hitsterGenerationPeriodIconUrl && (
                  <img
                    src={displayState.hitsterGenerationPeriodIconUrl}
                    alt=""
                    className="generation-display-icon"
                  />
                )}
                {displayState.hitsterGenerationPeriodLabel}
              </div>
            )}
            {displayState.mode === 'pubquiz' && roundName && (
              <div className="round-display">
                {roundName}
              </div>
            )}

            <div className="display-main">
          {/* Round Answers Mode */}
          {displayState.showRoundAnswers && displayState.roundAnswersSongs && displayState.roundAnswersSongs.length > 0 ? (
            <>
            <div className="display-round-answers">
              <div className="round-answers-title">
                🎵 Antwoorden {displayState.roundAnswersRoundName || roundName}
              </div>
              <div className="round-answers-strip-wrapper">
                <div className="round-answers-strip">
                  {(() => {
                    const startYear = displayState.roundAnswersStartYear;
                    const sorted = [...displayState.roundAnswersSongs].sort((a, b) => (a.year || 0) - (b.year || 0));
                    // Find insertion index: START card goes after all songs with year <= startYear
                    let insertIndex = startYear != null ? sorted.filter(s => (s.year || 0) <= startYear).length : null;
                    const items: React.ReactNode[] = [];
                    sorted.forEach((song, i) => {
                      if (startYear != null && i === insertIndex) {
                        items.push(
                          <div key="ra-start" className="ra-start-card">
                            <div className="ra-start-label">START</div>
                            <div className="ra-start-year">{startYear}</div>
                          </div>
                        );
                      }
                      items.push(
                        <div key={song.id} className="ra-card revealed" onClick={() => setZoomedCard({ order: song.order, year: song.year, title: song.title, artist: song.artist })}>
                          <div className="ra-card-number">Kaart {song.order}</div>
                          <div className="ra-card-year">{song.year}</div>
                          <div className="ra-card-content">
                            <div className="ra-card-title">{song.title}</div>
                            <div className="ra-card-artist">{song.artist}</div>
                          </div>
                        </div>
                      );
                    });
                    // If startYear is after all songs, append at end
                    if (startYear != null && insertIndex === sorted.length) {
                      items.push(
                        <div key="ra-start" className="ra-start-card">
                          <div className="ra-start-label">START</div>
                          <div className="ra-start-year">{startYear}</div>
                        </div>
                      );
                    }
                    return items;
                  })()}
                </div>
              </div>
              <div className="round-answers-hint-bottom">← Scroll om alle kaartjes te zien →</div>
            </div>

            {/* Zoomed card overlay */}
            {zoomedCard && (() => {
              const sorted = [...(displayState.roundAnswersSongs || [])].sort((a, b) => (a.year || 0) - (b.year || 0));
              const idx = sorted.findIndex(s => s.order === zoomedCard.order);
              const goTo = (i: number) => { const s = sorted[i]; setZoomedCard({ order: s.order, year: s.year, title: s.title, artist: s.artist }); };
              return (
                <div className="ra-zoom-overlay" onClick={() => setZoomedCard(null)}>
                  <button className="ra-zoom-nav ra-zoom-prev" disabled={idx <= 0} onClick={e => { e.stopPropagation(); if (idx > 0) goTo(idx - 1); }}>‹</button>
                  <div className="ra-zoom-card" onClick={e => e.stopPropagation()}>
                    <div className="ra-zoom-number">Kaart {zoomedCard.order} <span className="ra-zoom-counter">({idx + 1}/{sorted.length})</span></div>
                    <div className="ra-zoom-year">{zoomedCard.year}</div>
                    <div className="ra-zoom-title">{zoomedCard.title}</div>
                    <div className="ra-zoom-artist">{zoomedCard.artist}</div>
                    <button className="ra-zoom-close" onClick={() => setZoomedCard(null)}>✕</button>
                  </div>
                  <button className="ra-zoom-nav ra-zoom-next" disabled={idx >= sorted.length - 1} onClick={e => { e.stopPropagation(); if (idx < sorted.length - 1) goTo(idx + 1); }}>›</button>
                </div>
              );
            })()}
            </>
          ) : /* PubQuiz Mode */
          displayState.mode === 'pubquiz' && displayState.quizQuestion ? (
            <div className="pubquiz-display">
              <div className="quiz-question-container">
                {displayState.quizQuestion.imageUrl && (
                  displayState.quizQuestion.puzzleEnabled ? (
                    <PuzzleImage 
                      imageUrl={displayState.quizQuestion.imageUrl}
                      gridSize={displayState.quizQuestion.puzzleGridSize || 3}
                      speed={displayState.quizQuestion.puzzleSpeed || 'medium'}
                    />
                  ) : displayState.quizQuestion.spotlightEnabled ? (
                    <div className="spotlight-wrapper">
                      <img 
                        src={displayState.quizQuestion.imageUrl} 
                        alt="Vraag afbeelding" 
                        className="quiz-question-image"
                      />
                      <div 
                        className={`spotlight-overlay spotlight-speed-${displayState.quizQuestion.spotlightSpeed || 'medium'} spotlight-size-${displayState.quizQuestion.spotlightSize || 'medium'}`}
                      />
                    </div>
                  ) : (
                    <img 
                      src={displayState.quizQuestion.imageUrl} 
                      alt="Vraag afbeelding" 
                      className="quiz-question-image"
                    />
                  )
                )}
                <div className="quiz-question-text">
                  {displayState.quizQuestion.question}
                </div>
                
                {displayState.quizQuestion.type === 'multiple_choice' && displayState.quizQuestion.options && (
                  <div className="quiz-options">
                    {displayState.quizQuestion.options.map((option, index) => (
                      <div 
                        key={index} 
                        className={`quiz-option ${displayState.quizQuestion?.showAnswer && option === displayState.quizQuestion.correctAnswer ? 'correct' : ''}`}
                      >
                        <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                        <span className="option-text">{option}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {displayState.quizQuestion.type === 'true_false' && (
                  <div className="quiz-options true-false">
                    <div className={`quiz-option ${displayState.quizQuestion.showAnswer && displayState.quizQuestion.correctAnswer === 'Waar' ? 'correct' : ''}`}>
                      <span className="option-icon">✅</span>
                      <span className="option-text">Waar</span>
                    </div>
                    <div className={`quiz-option ${displayState.quizQuestion.showAnswer && displayState.quizQuestion.correctAnswer === 'Niet waar' ? 'correct' : ''}`}>
                      <span className="option-icon">❌</span>
                      <span className="option-text">Niet waar</span>
                    </div>
                  </div>
                )}

                {/* Rebus display */}
                {displayState.quizQuestion.type === 'rebus' && displayState.quizQuestion.rebusContent && (
                  <div className="rebus-display">
                    <div className="rebus-type-label">
                      {displayState.quizQuestion.rebusType === 'emoji' && '🎭 Emoji Raadsel'}
                      {displayState.quizQuestion.rebusType === 'missing_words' && '📝 Vul de missende woorden in'}
                      {displayState.quizQuestion.rebusType === 'scrambled' && '🔀 Ontcijfer de letters'}
                      {displayState.quizQuestion.rebusType === 'fragments' && '🎵 Herken de fragmenten'}
                    </div>
                    <div className={`rebus-content rebus-${displayState.quizQuestion.rebusType || 'emoji'}`}>
                      {displayState.quizQuestion.rebusContent}
                    </div>
                    {displayState.quizQuestion.rebusHints && displayState.quizQuestion.rebusHints.length > 0 && !displayState.quizQuestion.showAnswer && (
                      <div className="rebus-hints">
                        <div className="hints-label">💡 Hints:</div>
                        {displayState.quizQuestion.rebusHints.map((hint, i) => (
                          <div key={i} className="rebus-hint">{hint}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Video display */}
                {displayState.quizQuestion.type === 'video' && displayState.quizQuestion.youtubeVideoId && (
                  <div className="video-display">
                    <div className={`video-container ${displayState.quizQuestion.spotlightEnabled ? 'spotlight-active' : ''}`}>
                      <iframe
                        src={`https://www.youtube.com/embed/${displayState.quizQuestion.youtubeVideoId}?autoplay=1&rel=0${displayState.quizQuestion.youtubeStartTime ? `&start=${displayState.quizQuestion.youtubeStartTime}` : ''}${displayState.quizQuestion.youtubeEndTime ? `&end=${displayState.quizQuestion.youtubeEndTime}` : ''}`}
                        title="Video vraag"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      {displayState.quizQuestion.spotlightEnabled && (
                        <div 
                          className={`spotlight-overlay spotlight-speed-${displayState.quizQuestion.spotlightSpeed || 'medium'} spotlight-size-${displayState.quizQuestion.spotlightSize || 'medium'}`}
                        />
                      )}
                    </div>
                  </div>
                )}
                
                {displayState.quizQuestion.showAnswer && (
                  <div className="quiz-answer-reveal">
                    <div className="answer-label">✅ Het antwoord is:</div>
                    <div className="answer-text">{displayState.quizQuestion.correctAnswer}</div>
                  </div>
                )}
                
                <div className="quiz-question-meta">
                  <span className="quiz-points">{displayState.quizQuestion.points} punt{displayState.quizQuestion.points !== 1 ? 'en' : ''}</span>
                </div>
              </div>
            </div>
          ) : displayState.mode === 'bingo' && displayState.bingoView === 'hitster-spinner' ? (
            <div className="display-hitster-spinner-shell">
              <div className="display-hitster-spinner-wrap">
                {roundNumber > 0 && (
                  <div className="round-display round-display-inside">
                    {roundName || `Ronde #${roundNumber}`}
                  </div>
                )}
                {hitsterTipSecondsLeft !== null && (
                  <div className={`display-hitster-tip-badge${hitsterTipWarning ? ' warning' : ''}`}>
                    {hitsterTipSecondsLeft}s
                  </div>
                )}
                <div className="display-hitster-spinner">
                  <div
                    className="display-hitster-beam"
                    style={{
                      transform: `rotate(${bingoBeamAngle}deg)`,
                      transition: displayState.bingoIsSpinning
                        ? `transform ${displayState.bingoSpinDurationMs || 2600}ms cubic-bezier(0.15, 0.85, 0.2, 1)`
                        : 'transform 260ms ease-out'
                    }}
                    aria-hidden="true"
                  />

                  {bingoHitsterCategories.map((category, index) => {
                    const categoryCount = Math.max(bingoHitsterCategories.length, 1);
                    const previousCategory = bingoHitsterCategories[(index - 1 + categoryCount) % categoryCount];
                    const boundaryAngle = -90 - (180 / categoryCount) + index * (360 / categoryCount);
                    const prevColor = previousCategory?.color || '#8ad9ff';
                    const nextColor = category.color || '#8ad9ff';

                    return (
                      <div
                        key={`display-boundary-${category.id}`}
                        className="display-hitster-boundary-pair"
                        style={{ transform: `rotate(${boundaryAngle}deg)` }}
                      >
                        <span className="display-hitster-boundary-line" style={{ backgroundColor: prevColor }} />
                        <span className="display-hitster-boundary-line" style={{ backgroundColor: nextColor }} />
                      </div>
                    );
                  })}

                  {bingoHitsterCategories.map((category, index) => {
                    const angle = -90 + index * (360 / Math.max(bingoHitsterCategories.length, 1));
                    const radians = (angle * Math.PI) / 180;
                    const squareRadius = 250;
                    const symbolRadius = squareRadius;
                    const squareX = Math.cos(radians) * squareRadius;
                    const squareY = Math.sin(radians) * squareRadius;
                    const symbolX = Math.cos(radians) * symbolRadius;
                    const symbolY = Math.sin(radians) * symbolRadius;
                    const isActive = category.id === displayState.bingoSelectedCategoryId;
                    const color = category.color || '#8ad9ff';

                    return (
                      <React.Fragment key={category.id}>
                        <div
                          className={`display-hitster-category-tile ${isActive ? 'active' : ''}`}
                          style={{
                            left: `calc(50% + ${squareX}px)`,
                            top: `calc(50% + ${squareY}px)`,
                            borderColor: color,
                            boxShadow: `0 0 0 2px ${color}55, 0 0 22px ${color}44`
                          }}
                        >
                          <div className="display-hitster-category-square" style={{ backgroundColor: color }} />
                        </div>

                        <div
                          className={`display-hitster-symbol-marker ${isActive ? 'active' : ''}`}
                          style={{
                            left: `calc(50% + ${symbolX}px)`,
                            top: `calc(50% + ${symbolY}px)`,
                            color
                          }}
                        >
                          {isImageIcon(category.icon) ? (
                            <img className="display-hitster-symbol-icon-image" src={category.icon} alt={category.label} />
                          ) : (
                            <span className="display-hitster-symbol-icon-text">{category.icon || ''}</span>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  <div
                    className={`display-hitster-discoball${hitsterTipInnerWarning ? ' tip-warning-inner' : ''}${hitsterTipOuterWarning ? ' tip-warning-outer' : ''}`}
                    aria-hidden="true"
                  >
                    <span>🪩</span>
                  </div>
                </div>

                {/* Playing overlay: shown when a song is active */}
                {showResults && displayState.currentSong ? (
                  <div className="display-hitster-answer-overlay">
                    <div className="display-hitster-answer-label">✅ Het antwoord was:</div>
                    <div className="display-hitster-answer-title">{displayState.currentSong.title}</div>
                    <div className="display-hitster-answer-meta">
                      {displayState.currentSong.artist} ({displayState.currentSong.year})
                    </div>
                  </div>
                ) : displayState.currentSong && bingoSelectedCategory && !displayState.bingoIsSpinning && (
                  <div
                    className="display-hitster-playing-overlay"
                    style={{ borderColor: bingoSelectedCategory.color }}
                  >
                    <div
                      className="display-hitster-playing-category-bar"
                      style={{ backgroundColor: bingoSelectedCategory.color }}
                    >
                      {isImageIcon(bingoSelectedCategory.icon) ? (
                        <img
                          className="display-hitster-playing-icon"
                          src={bingoSelectedCategory.icon}
                          alt={bingoSelectedCategory.label}
                        />
                      ) : (
                        <span className="display-hitster-playing-icon-text">{bingoSelectedCategory.icon}</span>
                      )}
                      <span className="display-hitster-playing-category-name">{bingoSelectedCategory.label}</span>
                    </div>
                    <div className="display-hitster-playing-question">{bingoSelectedCategory.description}</div>
                    <div className="display-hitster-playing-instruction">🎵 Luister naar het nummer en beantwoord de vraag</div>
                    <div className="display-hitster-playing-hint">Kruis het vakje aan op je bingokaart</div>
                  </div>
                )}
              </div>
            </div>
          ) : showResults && currentSong ? (
            <div className="display-results">
              {currentSong.imageUrl && (
                <img
                  src={currentSong.imageUrl}
                  alt={currentSong.title}
                  className="display-image"
                />
              )}
              <div className="reveal-text">✅ Het antwoord was:</div>
              <div className="reveal-title">{currentSong.title}</div>
              <div className="reveal-artist">
                {currentSong.artist} ({currentSong.year})
              </div>
              {currentSong.yearSource && (
                <div className="reveal-year-source">
                  {/^https?:\/\//.test(currentSong.yearSource) ? (
                    <a href={currentSong.yearSource} target="_blank" rel="noopener noreferrer">
                      🔗 Bron verificatie jaar
                    </a>
                  ) : (
                    <span>📋 Bron: {currentSong.yearSource}</span>
                  )}
                </div>
              )}
            </div>
          ) : isIntermission ? (
            <div className="display-break">
              <div className="break-badge">Ingelaste Pauze</div>
              <div className="break-title">PAUZE</div>
              <div className="break-subtitle">
                {displayState.intermissionMessage || 'Tijd voor een drankje aan de bar'}
              </div>
            </div>
          ) : isPlaying && currentSong ? (
            <div className={`display-playing ${lyricsLines.length > 0 ? 'with-lyrics' : ''}`}>
              {/* Cymatics background animation */}
              <div className="cymatics-container">
                <div className="cymatics-ring ring-1"></div>
                <div className="cymatics-ring ring-2"></div>
                <div className="cymatics-ring ring-3"></div>
                <div className="cymatics-ring ring-4"></div>
                <div className="cymatics-wave wave-1"></div>
                <div className="cymatics-wave wave-2"></div>
                <div className="cymatics-wave wave-3"></div>
                <div className="cymatics-particle particle-1"></div>
                <div className="cymatics-particle particle-2"></div>
                <div className="cymatics-particle particle-3"></div>
                <div className="cymatics-particle particle-4"></div>
                <div className="cymatics-particle particle-5"></div>
                <div className="cymatics-particle particle-6"></div>
                <div className="cymatics-particle particle-7"></div>
                <div className="cymatics-particle particle-8"></div>
              </div>

              {/* Show listening banner only when no lyrics */}
              {lyricsLines.length === 0 && (
                <div className="listening-banner">
                  <span className="pulse-dot"></span>
                  <div className="listening-copy">
                    <span className="playing-text">🎵 Luister goed mee! 🎵</span>
                    <span className="singalong-text">meezingen mag 🙂</span>
                  </div>
                </div>
              )}

              {/* Lyrics display */}
              {lyricsLines.length > 0 && (
                <div className="karaoke-display" ref={karaokeRef}>
                  <div className="karaoke-text">
                    {lyricsLines.map((line, index) => (
                      <p 
                        key={index} 
                        className={`karaoke-line ${index === currentLineIndex ? 'active' : ''} ${index < currentLineIndex ? 'past' : ''}`}
                      >
                        {line.text}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="display-waiting">
              {/* QR Code Scanning Animation */}
              <div className="qr-scan-container">
                <div className="qr-code-wrapper">
                  {/* QR Code SVG - stylized QR pattern */}
                  <svg 
                    className="qr-code-svg" 
                    viewBox="0 0 200 200" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Background */}
                    <rect width="200" height="200" fill="white" rx="12"/>
                    
                    {/* Top-left corner pattern */}
                    <rect x="16" y="16" width="48" height="48" fill="#1a1a2e"/>
                    <rect x="24" y="24" width="32" height="32" fill="white"/>
                    <rect x="32" y="32" width="16" height="16" fill="#1a1a2e"/>
                    
                    {/* Top-right corner pattern */}
                    <rect x="136" y="16" width="48" height="48" fill="#1a1a2e"/>
                    <rect x="144" y="24" width="32" height="32" fill="white"/>
                    <rect x="152" y="32" width="16" height="16" fill="#1a1a2e"/>
                    
                    {/* Bottom-left corner pattern */}
                    <rect x="16" y="136" width="48" height="48" fill="#1a1a2e"/>
                    <rect x="24" y="144" width="32" height="32" fill="white"/>
                    <rect x="32" y="152" width="16" height="16" fill="#1a1a2e"/>
                    
                    {/* Data pattern - random dots */}
                    <rect x="80" y="16" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="16" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="16" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="80" y="32" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="32" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="80" y="48" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="48" width="8" height="8" fill="#1a1a2e"/>
                    
                    <rect x="16" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="32" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="48" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="16" y="96" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="48" y="96" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="16" y="112" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="32" y="112" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="48" y="112" width="8" height="8" fill="#1a1a2e"/>
                    
                    {/* Center data */}
                    <rect x="80" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="80" y="96" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="96" width="16" height="16" fill="#667eea"/>
                    <rect x="80" y="112" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="112" width="8" height="8" fill="#1a1a2e"/>
                    
                    {/* Right side data */}
                    <rect x="136" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="160" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="176" y="80" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="152" y="96" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="176" y="96" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="136" y="112" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="160" y="112" width="8" height="8" fill="#1a1a2e"/>
                    
                    {/* Bottom data */}
                    <rect x="80" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="80" y="152" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="152" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="80" y="168" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="96" y="168" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="112" y="168" width="8" height="8" fill="#1a1a2e"/>
                    
                    {/* Bottom-right corner data */}
                    <rect x="136" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="152" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="168" y="136" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="136" y="152" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="168" y="152" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="136" y="168" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="152" y="168" width="8" height="8" fill="#1a1a2e"/>
                    <rect x="176" y="168" width="8" height="8" fill="#1a1a2e"/>
                  </svg>
                  
                  {/* Scanning line animation */}
                  <div className="qr-scan-line"></div>
                </div>
              </div>
              
              <div className="scan-text">
                <span className="scan-icon">📱</span>
                {displayState.waitingMessage || (currentSongOrder ? `Nummer ${currentSongOrder} wordt gescand` : 'Een nieuw nummer wordt gescand')}
              </div>
            </div>
          )}
            </div>
            
          </>
        )}
      </div>
    </div>
  );
};

export default Display;
